import { useCallback } from 'react';
import { useI18n } from '@namespace/i18n';
import { useGetUserSegments } from '@namespace/segments';
import { gamesStoreActions, useGamesStore } from '../../store';
import { useGetPlatformForFeed } from '../useGetPlatformForFeed';

const { PLEX_GET_GAMES } = gamesStoreActions;

export const useGetLoadFeedGamesCallback = () => {
  const gamesFromStore = useGamesStore('games');

  const { language: lang } = useI18n();
  const { userSegmentsNames: userSegments } = useGetUserSegments();
  const platform = useGetPlatformForFeed();

  return useCallback(
    async ({ feedName, filter, dontUseGamesStorage = false }) => {
      const { id: filterIds, launchId: filterLaunchIds } = filter;

      // if no ids given (e.g. search - only name-filter) or dont need to use "cached" games-storage - just call BE
      if ((!filterIds && !filterLaunchIds) || dontUseGamesStorage) {
        return await PLEX_GET_GAMES({
          lang,
          feedName,
          platform,
          filter,
          userSegments,
          dontUseGamesStorage
        });
      }

      const notLoadedFilterIds = filterIds?.filter(
        (gameId) => !gamesFromStore.ids.includes(gameId)
      );
      const notLoadedFilterLaunchIds = filterLaunchIds?.filter(
        (launchId) => !([launchId] in gamesFromStore.launchIdToGameId)
      );

      // all games were loaded before, so return gameIds
      if (!notLoadedFilterIds?.length && !notLoadedFilterLaunchIds?.length) {
        const gamesIds =
          filterIds ||
          filterLaunchIds
            .map((launchId) => gamesFromStore.launchIdToGameId[launchId])
            .filter((item) => item);
        return {
          success: true,
          gamesIds,
          loadedGamesQty: gamesIds.length
        };
      }

      // call BE with only not previously loaded games
      const result = await PLEX_GET_GAMES({
        lang,
        feedName,
        platform,
        filter: {
          ...filter,
          [filterIds ? 'id' : 'launchId']: filterIds
            ? notLoadedFilterIds
            : notLoadedFilterLaunchIds
        },
        userSegments
      });

      const gamesIds =
        filterIds ||
        filterLaunchIds
          .map(
            (launchId) =>
              gamesFromStore.launchIdToGameId[launchId] ||
              result.newLaunchIdToGameId[launchId]
          )
          .filter((item) => item);

      return {
        ...result,
        gamesIds,
        loadedGamesQty: gamesIds.length
      };
    },
    [lang, platform, userSegments, gamesFromStore]
  );
};
